import type { FC } from 'react';
import { ProErrorPage } from '@features/error/views/ProErrorPage';
import * as Sentry from '@sentry/react';
import Nav from '@src/routes';

import type { DocType, ErrorPageProps, ErrorType } from '../types';
import { PPLErrorPage } from '../views/PPLErrorPage';

const client: string = import.meta.env?.VITE_CLIENT ?? process?.env?.CLIENT;
const isPPL = client === 'ct-ppl';

const HOME = {
  branding: Nav.Branding,
  contract: Nav.Submissions,
  template: Nav.Templates,
};

const ALLOWED_ERROR_CODES: DocType[] = ['branding', 'contract', 'template'];

interface ErrorControllerProps {
  docType?: DocType;
  resetError?: () => void;
  type: ErrorType;
}

const useErrorState = ({
  docType,
  type = 'unknown',
}: Pick<ErrorControllerProps, 'docType' | 'type'>): Pick<ErrorControllerProps, 'docType' | 'type'> & {
  originalUrl?: string;
} => {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');
  const fromParam = searchParams.get('from');

  let originalUrl: string | undefined;
  if (fromParam) {
    try {
      originalUrl = decodeURIComponent(fromParam);
    } catch (_error) {
      // Handle malformed URL encoding gracefully
      originalUrl = fromParam; // Fall back to the raw parameter
    }
  }

  if (code && ALLOWED_ERROR_CODES.includes(code as DocType)) {
    return { docType: code as DocType, type: 'document', originalUrl };
  }

  return { docType, type, originalUrl };
};

export const ErrorController: FC<ErrorControllerProps> = ({ resetError, ...props }) => {
  const { docType, type, originalUrl } = useErrorState(props);

  const handleRefresh = () => {
    resetError?.();
    window.location.reload();
  };

  const handleNavigateHome = () => {
    const homeURL = HOME[docType ?? 'contract'];
    window.location.href = homeURL;
  };

  const errorPageProps: ErrorPageProps = {
    onNavigateHome: handleNavigateHome,
    onRefresh: handleRefresh,
    docType,
    type,
    originalUrl,
  };

  if (type === '404') {
    Sentry.captureMessage('404 Page Not Found', {
      level: 'warning',
      extra: { originalUrl },
    });
  }

  if (isPPL) {
    return <PPLErrorPage {...errorPageProps} />;
  }

  return <ProErrorPage {...errorPageProps} />;
};
